import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { leaveService, leaveTypeService, userService } from '../../services/api';
import { FiPlus, FiX, FiDownload } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { exportToExcel } from '../../utils/exportExcel';
import { LeaveBalance } from './LeaveBalance';
import './LeaveList.css';

export const LeaveList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReadOnly = user?.isActive === false;
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(user?.id || '');
  const [filterType, setFilterType] = useState('all');
  const [leaveTypes, setLeaveTypes] = useState([]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchEmployees();
    }
  }, [user?.role]);

  useEffect(() => {
    fetchLeaves();
    fetchBalances();
    fetchLeaveTypes();
  }, [selectedEmployee, filterType]);

  const fetchEmployees = async () => {
    try {
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchLeaves = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await leaveService.getAll({
        userId: selectedEmployee,
        leaveTypeId: filterType === 'all' ? undefined : filterType,
      });
      setLeaves(response.data.leaves || []);
    } catch (err) {
      setError('Failed to load leave requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalances = async () => {
    try {
      const response = await leaveService.getBalance(selectedEmployee);
      setBalances(response.data || []);
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveTypeService.getAll({ limit: 1000 });
      setLeaveTypes(response.data || []);
    } catch (err) {
      console.error('Failed to fetch leave types:', err);
    }
  };

  const getLeaveTypeName = (typeId) => {
    const type = leaveTypes.find((t) => t.id === typeId);
    return type?.name || typeId;
  };

  const handleCancelLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      await leaveService.cancel(leaveId);
      setSuccess('Leave cancelled successfully');
      fetchLeaves();
      fetchBalances();
    } catch (err) {
      setError('Failed to cancel leave');
      console.error(err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'pending':
        return 'status-pending';
      case 'rejected':
        return 'status-rejected';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const canCancelLeave = (leave) => {
    return (
      leave.status === 'approved' ||
      leave.status === 'pending'
    );
  };

  return (
    <div className="leave-list-container">
      <div className="leave-header">
        <h1>Leave Management</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {leaves.length > 0 && (
            <button className="btn btn-secondary" onClick={() => {
              const data = leaves.map(l => ({
                Employee: l.User ? `${l.User.firstName} ${l.User.lastName}` : '',
                Type: l.LeaveType?.name || getLeaveTypeName(l.leaveTypeId),
                'Start Date': l.startDate,
                'End Date': l.endDate,
                Days: l.daysCount,
                Reason: l.reason,
                Status: l.status,
              }));
              exportToExcel(data, 'Leave_Requests');
            }}>
              <FiDownload /> Export
            </button>
          )}
          {!isReadOnly && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/leave/request')}
            >
              <FiPlus /> Request Leave
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="balances-section">
        <h2>Leave Balance</h2>
        <div className="balances-grid">
          {balances.length > 0 ? (
            balances.map((balance) => (
              <LeaveBalance key={balance.leaveTypeId} balance={balance} />
            ))
          ) : (
            <div className="no-data">No leave balance information available</div>
          )}
        </div>
      </div>

      {(user?.role === 'admin' || user?.role === 'manager') && (
        <div className="filters-section">
          <div className="filter-group">
            <label>Employee:</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value={user?.id}>My Leaves</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Type:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">All Types</option>
              {leaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading leaves...</div>
      ) : leaves.length > 0 ? (
        <div className="leaves-table-wrapper">
          <table className="leaves-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((leave) => (
                <tr key={leave.id}>
                  <td className="type-cell">
                    {leave.LeaveType?.name || getLeaveTypeName(leave.leaveTypeId)}
                  </td>
                  <td>{format(parseISO(leave.startDate), 'MMM d, yyyy')}</td>
                  <td>{format(parseISO(leave.endDate), 'MMM d, yyyy')}</td>
                  <td className="center">{leave.daysCount}</td>
                  <td className="reason-cell">{leave.reason}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(leave.status)}`}>
                      {leave.status}
                    </span>
                  </td>
                  <td>
                    {!isReadOnly && canCancelLeave(leave) && (
                      <button
                        className="btn-cancel"
                        onClick={() => handleCancelLeave(leave.id)}
                        title="Cancel leave"
                      >
                        <FiX />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-leaves">
          <p>No leave requests found</p>
          {!isReadOnly && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/leave/request')}
            >
              <FiPlus /> Request Leave
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaveList;
